import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const Table = ({ cols, rows, testid }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs" data-testid={testid}>
      <thead><tr className="text-left text-stone-500 border-b border-stone-200">{cols.map((c) => <th key={c} className="py-1 pr-3 font-medium">{c}</th>)}</tr></thead>
      <tbody>{rows}</tbody>
    </table>
  </div>
);

export const DataFlowsCard = ({ token }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/data-flows`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data); setError(null);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };
  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const scan = data?.scan;
  const clean = scan && !scan.unlisted_hosts.length && !scan.unlisted_storage_keys.length && !scan.document_cookie_uses.length && !scan.backend_set_cookie_uses.length;

  return (
    <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="data-flows-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-base font-semibold text-stone-800">Data flows and cookies</h3>
          <p className="text-xs text-stone-500">Every outside service the app sends data to, and every cookie / browser-storage key - generated from the code so it cannot drift. Read-only; no outbound call; no customer data.</p>
        </div>
        <Button size="sm" variant="outline" className="rounded-full" onClick={load} data-testid="data-flows-refresh">Refresh</Button>
      </div>
      {error && <p className="text-xs text-red-600" data-testid="data-flows-error">{error}</p>}
      {!data && !error && <p className="text-xs text-stone-400">Loading…</p>}
      {data && (
        <div className="space-y-5">
          <div className={`rounded-md px-3 py-2 text-xs ${clean ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-900'}`} data-testid="data-flows-scan-status">
            {clean ? `Scan clean: ${Object.keys(scan.hosts_found).length} hosts and ${Object.keys(scan.storage_keys_found).length} storage keys in ${scan.files_scanned} files, all listed; no cookie writes in app code.`
              : `Scan found items not yet listed - hosts: ${scan.unlisted_hosts.join(', ') || 'none'}; storage keys: ${scan.unlisted_storage_keys.join(', ') || 'none'}; cookie writes: ${[...scan.document_cookie_uses, ...scan.backend_set_cookie_uses].join(', ') || 'none'}.`}
            <span className="block text-[11px] opacity-70">Generated {data.generated_at?.slice(0, 16).replace('T', ' ')}Z · legal basis column: {data.legal_basis_placeholder}</span>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-stone-700 mb-1">Outbound data flows ({data.flows.length})</h4>
            <Table testid="data-flows-table" cols={["Service", "Hosts", "Status", "When", "What data", "Safeguards", "Legal basis"]} rows={data.flows.map((f) => (
              <tr key={f.id} className="border-b border-stone-100 align-top" data-testid={`data-flow-${f.id}`}>
                <td className="py-1.5 pr-3 font-medium text-stone-800 whitespace-nowrap">{f.service}<div className="text-[11px] text-stone-400 font-normal">{f.direction}</div></td>
                <td className="py-1.5 pr-3 font-mono text-[11px]">{f.hosts.join(', ') || '-'}</td>
                <td className="py-1.5 pr-3 whitespace-nowrap">{f.status}{f.customer_data ? <span className="block text-[10px] text-rose-700">customer data</span> : null}</td>
                <td className="py-1.5 pr-3 min-w-[12rem]">{f.when}</td>
                <td className="py-1.5 pr-3 min-w-[14rem]">{f.data}</td>
                <td className="py-1.5 pr-3 min-w-[12rem] text-stone-500">{f.safeguards}</td>
                <td className="py-1.5 pr-3 text-stone-500 min-w-[10rem]">{f.legal_basis}</td>
              </tr>
            ))} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-stone-700 mb-1">Browser storage keys ({data.storage.length})</h4>
            <Table testid="data-flows-storage-table" cols={["Key", "Store", "Set by", "Lifetime", "Purpose", "Personal?"]} rows={data.storage.map((s) => (
              <tr key={s.key} className="border-b border-stone-100 align-top" data-testid={`storage-key-${s.key}`}>
                <td className="py-1.5 pr-3 font-mono text-[11px]">{s.key}</td><td className="py-1.5 pr-3">{s.store}</td><td className="py-1.5 pr-3">{s.set_by}</td>
                <td className="py-1.5 pr-3">{s.lifetime}</td><td className="py-1.5 pr-3">{s.purpose}</td><td className="py-1.5 pr-3">{s.personal}</td>
              </tr>
            ))} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-stone-700 mb-1">Cookies ({data.cookies.length})</h4>
            <Table testid="data-flows-cookies-table" cols={["Name", "Set by", "Lifetime", "Purpose", "Personal?"]} rows={data.cookies.map((c) => (
              <tr key={c.name} className="border-b border-stone-100 align-top" data-testid={`cookie-${c.name.replace(/[^a-z0-9_]/gi, '')}`}>
                <td className="py-1.5 pr-3 font-mono text-[11px]">{c.name}</td><td className="py-1.5 pr-3">{c.set_by}</td><td className="py-1.5 pr-3">{c.lifetime}</td>
                <td className="py-1.5 pr-3">{c.purpose}</td><td className="py-1.5 pr-3">{c.personal}</td>
              </tr>
            ))} />
          </div>
          <p className="text-[11px] text-stone-400">{data.note}</p>
        </div>
      )}
    </div>
  );
};
